import React, { useState, useEffect } from 'react';
import useNarrow77, { PRESET_DISCIPLINES, DAY_SHORT, getDateKey, getDaysBetween, appliesToDate } from './hooks/useNarrow77';

// ─── Design Tokens ──────────────────────────────────────────────
const T = {
  bg:         '#0d0b09',
  surface:    '#16130f',
  card:       '#1c1914',
  cardHover:  '#211e17',
  cardDone:   'rgba(122, 139, 106, 0.06)',
  text:       '#e8e0d4',
  textSoft:   '#a69882',
  textMuted:  '#5c5347',
  gold:       '#c8a45c',
  goldDim:    'rgba(200, 164, 92, 0.12)',
  goldBorder: 'rgba(200, 164, 92, 0.08)',
  sage:       '#7a8b6a',
  sageDim:    'rgba(122, 139, 106, 0.15)',
  sageBorder: 'rgba(122, 139, 106, 0.25)',
  danger:     '#8a4a4a',
  dangerDim:  'rgba(138, 74, 74, 0.1)',
  warn:       '#b8895a',
  warnDim:    'rgba(184, 137, 90, 0.1)',
  border:     'rgba(200, 164, 92, 0.06)',
  font:       "'Karla', sans-serif",
  serif:      "'Crimson Pro', Georgia, serif",
  radius:     12,
};

// ─── Overlay ────────────────────────────────────────────────────
const Overlay = ({ children, onClose }) => (
  <div style={styles.overlay} onClick={onClose}>
    <div style={styles.modal} onClick={e => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// SETUP SCREEN
// ═══════════════════════════════════════════════════════════════════
const SetupScreen = ({ onStart }) => {
  const [selected, setSelected] = useState({});
  const [customDisciplines, setCustomDisciplines] = useState([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customDesc, setCustomDesc] = useState('');
  const [customVerse, setCustomVerse] = useState('');
  const [customFreq, setCustomFreq] = useState('daily');
  const [customWeeklyDay, setCustomWeeklyDay] = useState(5); // Friday default
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { setLoaded(true); }, []);

  const togglePreset = (id) => {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const setFrequency = (id, freq) => {
    setSelected(prev => ({ ...prev, [`${id}_freq`]: freq }));
  };

  const setWeeklyDay = (id, day) => {
    setSelected(prev => ({ ...prev, [`${id}_day`]: day }));
  };

  const addCustom = () => {
    if (!customTitle.trim()) return;
    const id = 'custom_' + Date.now();
    setCustomDisciplines(prev => [...prev, {
      id,
      title: customTitle.trim(),
      description: customDesc.trim() || 'A personal discipline.',
      verse: customVerse.trim() || '',
      frequency: customFreq,
      weeklyDay: customFreq === 'weekly' ? customWeeklyDay : undefined,
      isCustom: true,
    }]);
    setSelected(prev => ({ ...prev, [id]: true }));
    setCustomTitle('');
    setCustomDesc('');
    setCustomVerse('');
    setCustomFreq('daily');
    setCustomWeeklyDay(5);
    setShowCustomForm(false);
  };

  const removeCustom = (id) => {
    setCustomDisciplines(prev => prev.filter(d => d.id !== id));
    setSelected(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleStart = () => {
    const disciplines = [];

    // Add selected presets
    PRESET_DISCIPLINES.forEach(d => {
      if (selected[d.id]) {
        const freq = selected[`${d.id}_freq`] || d.frequency;
        disciplines.push({
          ...d,
          frequency: freq,
          weeklyDay: freq === 'weekly' ? (selected[`${d.id}_day`] ?? 5) : undefined,
        });
      }
    });

    // Add custom
    customDisciplines.forEach(d => {
      if (selected[d.id]) {
        disciplines.push(d);
      }
    });

    if (disciplines.length === 0) return;
    onStart(disciplines);
  };

  const selectedCount = [...PRESET_DISCIPLINES, ...customDisciplines].filter(d => selected[d.id]).length;

  return (
    <div style={styles.shell}>
      <div style={styles.grain} />
      <div style={styles.narrowLine} />
      <div style={{ ...styles.container, justifyContent: 'flex-start' }}>
        <div style={styles.setupScroll}>
          {/* Header */}
          <div style={{
            textAlign: 'center',
            padding: 'max(50px, env(safe-area-inset-top, 50px)) 0 24px',
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6, marginBottom: 8 }}>
              <h1 style={{ fontFamily: T.serif, fontSize: 28, fontWeight: 300, letterSpacing: 6, color: T.text, margin: 0 }}>NARROW</h1>
              <span style={{ fontFamily: T.serif, fontSize: 28, fontWeight: 600, color: T.gold, letterSpacing: 2 }}>77</span>
            </div>
            <p style={{ fontFamily: T.serif, fontStyle: 'italic', fontSize: 15, color: T.textMuted, margin: '0 0 6px' }}>
              "Enter through the narrow gate."
            </p>
            <p style={{ fontSize: 11, letterSpacing: 3, color: T.textMuted }}>BUILD YOUR CHALLENGE</p>
          </div>

          {/* Instructions */}
          <div style={{
            padding: '0 4px 20px',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.5s ease 0.2s',
          }}>
            <p style={{ fontFamily: T.serif, fontSize: 15, color: T.textSoft, textAlign: 'center', lineHeight: 1.6 }}>
              Choose your disciplines. Complete every one, every day, for 77 days. Miss one — start over.
            </p>
          </div>

          {/* Preset Disciplines */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {PRESET_DISCIPLINES.map((disc, i) => {
              const isSelected = selected[disc.id];
              const freq = selected[`${disc.id}_freq`] || disc.frequency;
              return (
                <div key={disc.id} style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(12px)',
                  transition: `all 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${0.1 + i * 0.03}s`,
                }}>
                  <div
                    onClick={() => togglePreset(disc.id)}
                    style={{
                      ...styles.setupCard,
                      ...(isSelected ? styles.setupCardActive : {}),
                    }}
                  >
                    <div style={{
                      ...styles.setupCheck,
                      ...(isSelected ? styles.setupCheckActive : {}),
                    }}>
                      {isSelected && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke={T.sage} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                        <span style={{ fontFamily: T.serif, fontSize: 17, fontWeight: 500, color: isSelected ? T.sage : T.text }}>{disc.title}</span>
                        {disc.verse && <span style={{ fontSize: 10, color: T.textMuted, whiteSpace: 'nowrap', letterSpacing: 0.5, flexShrink: 0 }}>{disc.verse}</span>}
                      </div>
                      <p style={{ fontSize: 12, color: T.textSoft, margin: 0, lineHeight: 1.5 }}>{disc.description}</p>
                    </div>
                  </div>

                  {/* Frequency toggle — shown when selected */}
                  {isSelected && (
                    <div style={styles.freqRow}>
                      <span style={{ fontSize: 11, color: T.textMuted, letterSpacing: 1 }}>FREQUENCY:</span>
                      <div style={styles.freqToggle}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setFrequency(disc.id, 'daily'); }}
                          style={{ ...styles.freqBtn, ...(freq === 'daily' ? styles.freqBtnActive : {}) }}
                        >DAILY</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setFrequency(disc.id, 'weekly'); }}
                          style={{ ...styles.freqBtn, ...(freq === 'weekly' ? styles.freqBtnActive : {}) }}
                        >WEEKLY</button>
                      </div>
                    </div>
                  )}
                  {isSelected && freq === 'weekly' && (
                    <div style={styles.freqRow}>
                      <span style={{ fontSize: 11, color: T.textMuted, letterSpacing: 1 }}>DAY:</span>
                      <div style={styles.freqToggle}>
                        {DAY_SHORT.map((name, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => { e.stopPropagation(); setWeeklyDay(disc.id, idx); }}
                            style={{ ...styles.dayBtn, ...((selected[`${disc.id}_day`] ?? 5) === idx ? styles.dayBtnActive : {}) }}
                          >{name}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Custom Disciplines */}
          {customDisciplines.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: 10, letterSpacing: 3, color: T.textMuted, marginBottom: 10, fontWeight: 600 }}>CUSTOM</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {customDisciplines.map(disc => (
                  <div key={disc.id} style={{ ...styles.setupCard, ...styles.setupCardActive }}>
                    <div style={{ ...styles.setupCheck, ...styles.setupCheckActive }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke={T.sage} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontFamily: T.serif, fontSize: 17, fontWeight: 500, color: T.sage }}>{disc.title}</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ fontSize: 10, color: T.gold, letterSpacing: 1 }}>{disc.frequency.toUpperCase()}</span>
                          <button onClick={() => removeCustom(disc.id)} style={{ background: 'none', border: 'none', color: T.danger, cursor: 'pointer', fontSize: 14, padding: 2 }}>✕</button>
                        </div>
                      </div>
                      {disc.description && <p style={{ fontSize: 12, color: T.textSoft, margin: '3px 0 0', lineHeight: 1.5 }}>{disc.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Custom Button / Form */}
          <div style={{ marginTop: 16 }}>
            {!showCustomForm ? (
              <button onClick={() => setShowCustomForm(true)} style={styles.addCustomBtn}>
                + ADD CUSTOM DISCIPLINE
              </button>
            ) : (
              <div style={styles.customForm}>
                <input
                  value={customTitle}
                  onChange={e => setCustomTitle(e.target.value)}
                  placeholder="Discipline name *"
                  style={styles.input}
                  autoFocus
                />
                <input
                  value={customDesc}
                  onChange={e => setCustomDesc(e.target.value)}
                  placeholder="Description (optional)"
                  style={styles.input}
                />
                <input
                  value={customVerse}
                  onChange={e => setCustomVerse(e.target.value)}
                  placeholder="Verse reference (optional)"
                  style={styles.input}
                />
                <div style={styles.freqRow}>
                  <span style={{ fontSize: 11, color: T.textMuted, letterSpacing: 1 }}>FREQUENCY:</span>
                  <div style={styles.freqToggle}>
                    <button
                      onClick={() => setCustomFreq('daily')}
                      style={{ ...styles.freqBtn, ...(customFreq === 'daily' ? styles.freqBtnActive : {}) }}
                    >DAILY</button>
                    <button
                      onClick={() => setCustomFreq('weekly')}
                      style={{ ...styles.freqBtn, ...(customFreq === 'weekly' ? styles.freqBtnActive : {}) }}
                    >WEEKLY</button>
                  </div>
                </div>
                {customFreq === 'weekly' && (
                  <div style={styles.freqRow}>
                    <span style={{ fontSize: 11, color: T.textMuted, letterSpacing: 1 }}>DAY:</span>
                    <div style={styles.freqToggle}>
                      {DAY_SHORT.map((name, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCustomWeeklyDay(idx)}
                          style={{ ...styles.dayBtn, ...(customWeeklyDay === idx ? styles.dayBtnActive : {}) }}
                        >{name}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={addCustom} style={{ ...styles.btnPrimary, flex: 1 }}>ADD</button>
                  <button onClick={() => setShowCustomForm(false)} style={{ ...styles.btnMuted, flex: 1 }}>CANCEL</button>
                </div>
              </div>
            )}
          </div>

          {/* Start Button */}
          <div style={{ padding: '24px 0 40px' }}>
            <button
              onClick={handleStart}
              disabled={selectedCount === 0}
              style={{
                ...styles.startBtn,
                opacity: selectedCount === 0 ? 0.3 : 1,
                cursor: selectedCount === 0 ? 'default' : 'pointer',
              }}
            >
              BEGIN THE CHALLENGE — {selectedCount} DISCIPLINE{selectedCount !== 1 ? 'S' : ''}
            </button>
            <p style={{ textAlign: 'center', fontSize: 12, color: T.textMuted, marginTop: 8 }}>
              77 days. No compromise.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════
const App = () => {
  const hook = useNarrow77();

  // Setup screen
  if (hook.needsSetup) {
    return <SetupScreen onStart={hook.initChallenge} />;
  }

  return <MainTracker {...hook} />;
};

const MainTracker = ({
  data, today, currentDay, totalDays, streak, disciplines,
  toggleDiscipline, saveJournal, restartChallenge, fullReset,
  updateDisciplines, isDayComplete, isDisciplineFulfilled, getDisciplinesForDate,
}) => {
  const [selectedDate, setSelectedDate] = useState(today);
  const [journalText, setJournalText] = useState('');
  const [modal, setModal] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { setLoaded(true); }, []);

  useEffect(() => {
    const dayData = data.days[selectedDate];
    setJournalText(dayData?.journal || '');
  }, [selectedDate, data]);

  const dayData = data.days[selectedDate] || { checked: {}, journal: '' };
  const todaysDisciplines = getDisciplinesForDate(selectedDate);
  const checkedCount = todaysDisciplines.filter(d => isDisciplineFulfilled(selectedDate, d)).length;
  const allComplete = todaysDisciplines.length > 0 && checkedCount === todaysDisciplines.length;
  const progressPct = Math.min(((currentDay - 1) / totalDays) * 100, 100);
  const selectedDayNum = getDaysBetween(data.startDate, selectedDate) + 1;

  const goDate = (offset) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + offset);
    const key = getDateKey(d);
    if (key <= today) setSelectedDate(key);
  };

  // Build week
  const weekDays = [];
  for (let i = -3; i <= 3; i++) {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + i);
    const key = getDateKey(d);
    const complete = isDayComplete(key);
    const dd = data.days[key];
    const dayDiscs = getDisciplinesForDate(key);
    const partial = dd && dayDiscs.some(disc => isDisciplineFulfilled(key, disc)) && !complete;
    weekDays.push({ key, complete, partial, isFuture: key > today, isSelected: key === selectedDate, date: d });
  }

  const handleSaveJournal = () => {
    saveJournal(selectedDate, journalText);
    setModal(null);
  };

  const handleRestart = () => {
    restartChallenge();
    setSelectedDate(getDateKey(new Date()));
    setModal(null);
  };

  const handleFullReset = () => {
    fullReset();
  };

  return (
    <div style={styles.shell}>
      <div style={styles.grain} />
      <div style={styles.narrowLine} />

      <div style={styles.container}>
        {/* ─── Header ──────────────────────── */}
        <header style={{
          ...styles.header,
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(-10px)',
          transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          <div style={styles.headerTop}>
            <div style={styles.brand}>
              <h1 style={styles.title}>NARROW</h1>
              <span style={styles.titleNum}>77</span>
            </div>
            <div style={styles.headerActions}>
              <button onClick={() => window.open(window.location.origin + '/download.html', '_blank')} style={styles.headerBtn} aria-label="Download">
                <span style={{ fontSize: 14 }}>↓</span>
              </button>
              <button onClick={() => setModal('info')} style={styles.headerBtn} aria-label="About">
                <span style={{ fontSize: 15 }}>?</span>
              </button>
              <button onClick={() => setModal('history')} style={styles.headerBtn} aria-label="History">
                <span style={{ fontSize: 13 }}>☰</span>
              </button>
              <button onClick={() => setModal('reset')} style={styles.headerBtn} aria-label="Reset">
                <span style={{ fontSize: 15 }}>↺</span>
              </button>
            </div>
          </div>

          {/* Stats row */}
          <div style={styles.statsRow}>
            <div style={styles.stat}>
              <span style={styles.statValue}>{currentDay}</span>
              <span style={styles.statLabel}>DAY</span>
            </div>
            <div style={styles.statDivider} />
            <div style={styles.stat}>
              <span style={styles.statValue}>{totalDays - currentDay}</span>
              <span style={styles.statLabel}>REMAINING</span>
            </div>
            <div style={styles.statDivider} />
            <div style={styles.stat}>
              <span style={styles.statValue}>{streak}</span>
              <span style={styles.statLabel}>STREAK</span>
            </div>
            {data.restarts > 0 && (
              <>
                <div style={styles.statDivider} />
                <div style={styles.stat}>
                  <span style={{ ...styles.statValue, color: T.warn }}>{data.restarts}</span>
                  <span style={styles.statLabel}>RESTART{data.restarts > 1 ? 'S' : ''}</span>
                </div>
              </>
            )}
          </div>

          {/* Progress bar */}
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
          </div>
        </header>

        {/* ─── Week Nav ────────────────────── */}
        <nav style={{
          ...styles.weekNav,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.6s ease 0.2s',
        }}>
          <button onClick={() => goDate(-1)} style={styles.weekArrow}>‹</button>
          <div style={styles.weekDays}>
            {weekDays.map(wd => {
              const isToday = wd.key === today;
              return (
                <button
                  key={wd.key}
                  onClick={() => !wd.isFuture && setSelectedDate(wd.key)}
                  style={{
                    ...styles.weekDay,
                    ...(wd.isSelected ? styles.weekDayActive : {}),
                    ...(wd.complete && !wd.isSelected ? { color: T.sage } : {}),
                    ...(wd.partial && !wd.isSelected ? { color: T.textSoft } : {}),
                    ...(wd.isFuture ? { opacity: 0.2, cursor: 'default' } : {}),
                  }}
                >
                  <span style={styles.weekDayLabel}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'][wd.date.getDay()]}
                  </span>
                  <span style={styles.weekDayNum}>{wd.date.getDate()}</span>
                  {isToday && <div style={styles.todayDot} />}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => goDate(1)}
            style={{ ...styles.weekArrow, opacity: selectedDate >= today ? 0.15 : 0.6 }}
          >›</button>
        </nav>

        {/* ─── Complete Banner ─────────────── */}
        {selectedDate === today && allComplete && (
          <div style={styles.completeBanner}>
            <div style={styles.completeInner}>
              ✦&nbsp;&nbsp;ALL {todaysDisciplines.length} COMPLETE&nbsp;&nbsp;✦
            </div>
          </div>
        )}

        {/* ─── Disciplines ─────────────────── */}
        <div style={styles.listArea}>
          <div style={styles.list}>
            {todaysDisciplines.map((disc, i) => {
              const fulfilled = isDisciplineFulfilled(selectedDate, disc);
              const isWeekly = disc.frequency === 'weekly';
              return (
                <div
                  key={disc.id}
                  onClick={() => toggleDiscipline(selectedDate, disc.id)}
                  style={{
                    ...styles.card,
                    ...(fulfilled ? styles.cardChecked : {}),
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'translateY(0)' : 'translateY(12px)',
                    transition: `all 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${0.1 + i * 0.05}s`,
                  }}
                >
                  <div style={{
                    ...styles.check,
                    ...(fulfilled ? styles.checkActive : {}),
                  }}>
                    {fulfilled && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke={T.sage} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div style={styles.cardContent}>
                    <div style={styles.cardTop}>
                      <span style={{
                        ...styles.cardTitle,
                        ...(fulfilled ? { color: T.sage } : {}),
                      }}>{disc.title}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {isWeekly && (
                          <span style={{
                            fontSize: 9,
                            letterSpacing: 1.5,
                            color: fulfilled ? T.sage : T.gold,
                            fontWeight: 600,
                            background: fulfilled ? T.sageDim : T.goldDim,
                            padding: '2px 6px',
                            borderRadius: 4,
                          }}>{DAY_SHORT[disc.weeklyDay] || 'WEEKLY'}</span>
                        )}
                        {disc.verse && <span style={styles.cardVerse}>{disc.verse}</span>}
                      </div>
                    </div>
                    <p style={styles.cardDesc}>{disc.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Journal button */}
          <div style={{
            ...styles.journalArea,
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.5s ease 0.6s',
          }}>
            <button onClick={() => setModal('journal')} style={styles.journalBtn}>
              <span style={styles.journalIcon}>✎</span>
              {dayData.journal ? 'EDIT JOURNAL' : 'WRITE IN JOURNAL'}
              {dayData.journal && <span style={styles.journalDot} />}
            </button>
          </div>

          {/* Counter */}
          <div style={styles.counter}>
            <span style={styles.counterNum}>{checkedCount}</span>
            <span style={styles.counterSlash}>/</span>
            <span style={styles.counterTotal}>{todaysDisciplines.length}</span>
          </div>

          {/* Scripture quote */}
          <p style={styles.footerVerse}>
            "Enter through the narrow gate. For wide is the gate and broad is the road that leads to destruction."
          </p>
          <p style={styles.footerRef}>Matthew 7:13</p>

          <div style={{ height: 40 }} />
        </div>
      </div>

      {/* ═══════ MODALS ═══════════════════════════════════════════ */}

      {/* Journal */}
      {modal === 'journal' && (
        <Overlay onClose={() => setModal(null)}>
          <div style={styles.modalHeader}>
            <div>
              <h3 style={styles.modalTitle}>Journal</h3>
              <p style={styles.modalSub}>Day {selectedDayNum > 0 ? selectedDayNum : '—'} · {selectedDate}</p>
            </div>
            <button onClick={() => setModal(null)} style={styles.modalClose}>✕</button>
          </div>
          <textarea
            value={journalText}
            onChange={e => setJournalText(e.target.value)}
            placeholder="Examine your day. What did God reveal? Where were you challenged? What are you grateful for?"
            style={styles.textarea}
            autoFocus
          />
          <button onClick={handleSaveJournal} style={styles.btnPrimary}>SAVE ENTRY</button>
        </Overlay>
      )}

      {/* Reset */}
      {modal === 'reset' && (
        <Overlay onClose={() => setModal(null)}>
          <div style={styles.modalHeader}>
            <h3 style={styles.modalTitle}>Restart Challenge</h3>
            <button onClick={() => setModal(null)} style={styles.modalClose}>✕</button>
          </div>
          <p style={{ ...styles.modalSub, padding: '0 0 20px' }}>
            Missed a discipline? Be honest with yourself. Restart with integrity.
          </p>
          <button onClick={handleRestart} style={styles.btnWarn}>RESTART FROM DAY 1</button>
          <div style={{ height: 10 }} />
          <button onClick={() => setModal('editDisciplines')} style={styles.btnMuted}>EDIT MY DISCIPLINES</button>
          <div style={{ height: 10 }} />
          <button onClick={handleFullReset} style={styles.btnDanger}>FULL RESET — START OVER COMPLETELY</button>
        </Overlay>
      )}

      {/* Edit Disciplines */}
      {modal === 'editDisciplines' && (
        <EditDisciplinesModal
          disciplines={disciplines}
          onSave={(updated) => { updateDisciplines(updated); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}

      {/* History */}
      {modal === 'history' && (
        <Overlay onClose={() => setModal(null)}>
          <div style={styles.modalHeader}>
            <h3 style={styles.modalTitle}>History</h3>
            <button onClick={() => setModal(null)} style={styles.modalClose}>✕</button>
          </div>
          <div style={styles.historyList}>
            {Object.keys(data.days).length === 0 ? (
              <p style={{ color: T.textMuted, textAlign: 'center', padding: '40px 0', fontStyle: 'italic' }}>
                No entries yet. Begin today.
              </p>
            ) : (
              Object.keys(data.days).sort().reverse().map(dateKey => {
                const complete = isDayComplete(dateKey);
                const dateDisciplines = getDisciplinesForDate(dateKey);
                const count = dateDisciplines.filter(d => isDisciplineFulfilled(dateKey, d)).length;
                const dd = data.days[dateKey];
                const dn = getDaysBetween(data.startDate, dateKey) + 1;
                return (
                  <div
                    key={dateKey}
                    onClick={() => { setSelectedDate(dateKey); setModal(null); }}
                    style={styles.historyRow}
                  >
                    <div style={styles.historyLeft}>
                      <span style={styles.historyDay}>Day {dn > 0 ? dn : '—'}</span>
                      <span style={styles.historyDate}>{dateKey}</span>
                    </div>
                    <div style={styles.historyRight}>
                      <span style={{ color: complete ? T.sage : T.textMuted }}>{count}/{dateDisciplines.length}</span>
                      {complete && <span style={{ color: T.gold, marginLeft: 6 }}>✦</span>}
                      {dd.journal && <span style={{ marginLeft: 6, color: T.textMuted, fontSize: 12 }}>✎</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Overlay>
      )}

      {/* Info / About */}
      {modal === 'info' && (
        <Overlay onClose={() => setModal(null)}>
          <div style={styles.modalHeader}>
            <h3 style={styles.modalTitle}>About Narrow77</h3>
            <button onClick={() => setModal(null)} style={styles.modalClose}>✕</button>
          </div>
          <div style={styles.infoContent}>
            <p style={styles.infoVerse}>
              "Enter through the narrow gate. For wide is the gate and broad is the road that leads to destruction, and many enter through it. But small is the gate and narrow the road that leads to life, and only a few find it."
            </p>
            <p style={styles.infoRef}>— Matthew 7:13-14</p>

            <h4 style={styles.infoHeading}>The Challenge</h4>
            <p style={styles.infoText}>
              Complete all your chosen disciplines every day for 77 days. Miss one? Start over from Day 1. No exceptions.
            </p>

            <h4 style={styles.infoHeading}>Your Disciplines</h4>
            {disciplines.map(d => (
              <div key={d.id} style={styles.infoItem}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={styles.infoItemTitle}>{d.title}</span>
                  {d.frequency === 'weekly' && (
                    <span style={{ fontSize: 9, letterSpacing: 1, color: T.gold, fontWeight: 600 }}>{DAY_SHORT[d.weeklyDay] || 'WEEKLY'}</span>
                  )}
                </div>
                <span style={styles.infoItemDesc}>{d.description}</span>
              </div>
            ))}

            <h4 style={styles.infoHeading}>Why 77?</h4>
            <p style={styles.infoText}>
              7 represents completion in scripture — God rested on the 7th day, there are 7 churches in Revelation, 7 seals, 7 trumpets. Jesus told Peter to forgive "seventy-seven times" (Matthew 18:22). 77 days of discipline. Enough time to build real transformation, not just motivation.
            </p>

            <h4 style={styles.infoHeading}>Legal</h4>
            <p style={styles.infoText}>
              <span onClick={() => window.open(window.location.origin + '/terms.html', '_blank')} style={{ color: T.gold, cursor: 'pointer' }}>Terms of Service</span>
              {' · '}
              <span onClick={() => window.open(window.location.origin + '/privacy.html', '_blank')} style={{ color: T.gold, cursor: 'pointer' }}>Privacy Policy</span>
            </p>
          </div>
        </Overlay>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// EDIT DISCIPLINES MODAL
// ═══════════════════════════════════════════════════════════════════
const EditDisciplinesModal = ({ disciplines, onSave, onClose }) => {
  const [list, setList] = useState(disciplines.map(d => ({ ...d })));
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [verse, setVerse] = useState('');
  const [freq, setFreq] = useState('daily');
  const [editWeeklyDay, setEditWeeklyDay] = useState(5);

  const remove = (id) => setList(prev => prev.filter(d => d.id !== id));

  const toggleFreq = (id) => {
    setList(prev => prev.map(d => d.id === id ? {
      ...d,
      frequency: d.frequency === 'daily' ? 'weekly' : 'daily',
      weeklyDay: d.frequency === 'daily' ? (d.weeklyDay ?? 5) : undefined,
    } : d));
  };

  const setDayForDisc = (id, day) => {
    setList(prev => prev.map(d => d.id === id ? { ...d, weeklyDay: day } : d));
  };

  const addCustom = () => {
    if (!title.trim()) return;
    setList(prev => [...prev, {
      id: 'custom_' + Date.now(),
      title: title.trim(),
      description: desc.trim() || 'A personal discipline.',
      verse: verse.trim() || '',
      frequency: freq,
      weeklyDay: freq === 'weekly' ? editWeeklyDay : undefined,
      isCustom: true,
    }]);
    setTitle(''); setDesc(''); setVerse(''); setFreq('daily'); setEditWeeklyDay(5); setShowAdd(false);
  };

  return (
    <Overlay onClose={onClose}>
      <div style={styles.modalHeader}>
        <h3 style={styles.modalTitle}>Edit Disciplines</h3>
        <button onClick={onClose} style={styles.modalClose}>✕</button>
      </div>
      <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>
        Remove, add, or change frequency. Changes apply going forward.
      </p>
      <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
        {list.map(d => (
          <div key={d.id} style={{ padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontFamily: T.serif, fontSize: 15, color: T.text }}>{d.title}</span>
              </div>
              <button onClick={() => toggleFreq(d.id)} style={{
                fontSize: 9, letterSpacing: 1, fontWeight: 600, padding: '3px 8px', borderRadius: 4, cursor: 'pointer', fontFamily: T.font, border: 'none',
                background: d.frequency === 'weekly' ? T.goldDim : T.sageDim,
                color: d.frequency === 'weekly' ? T.gold : T.sage,
              }}>{d.frequency === 'weekly' ? DAY_SHORT[d.weeklyDay] || 'WEEKLY' : 'DAILY'}</button>
              <button onClick={() => remove(d.id)} style={{ background: 'none', border: 'none', color: T.danger, cursor: 'pointer', fontSize: 14, padding: '2px 4px' }}>✕</button>
            </div>
            {d.frequency === 'weekly' && (
              <div style={{ ...styles.freqRow, paddingLeft: 0, paddingTop: 6 }}>
                <span style={{ fontSize: 10, color: T.textMuted, letterSpacing: 1 }}>DAY:</span>
                <div style={styles.freqToggle}>
                  {DAY_SHORT.map((name, idx) => (
                    <button
                      key={idx}
                      onClick={() => setDayForDisc(d.id, idx)}
                      style={{ ...styles.dayBtn, ...((d.weeklyDay ?? 5) === idx ? styles.dayBtnActive : {}) }}
                    >{name}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {!showAdd ? (
        <button onClick={() => setShowAdd(true)} style={{ ...styles.addCustomBtn, marginTop: 12 }}>
          + ADD DISCIPLINE
        </button>
      ) : (
        <div style={{ ...styles.customForm, marginTop: 12 }}>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Discipline name *" style={styles.input} autoFocus />
          <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)" style={styles.input} />
          <input value={verse} onChange={e => setVerse(e.target.value)} placeholder="Verse reference (optional)" style={styles.input} />
          <div style={styles.freqRow}>
            <span style={{ fontSize: 11, color: T.textMuted, letterSpacing: 1 }}>FREQUENCY:</span>
            <div style={styles.freqToggle}>
              <button onClick={() => setFreq('daily')} style={{ ...styles.freqBtn, ...(freq === 'daily' ? styles.freqBtnActive : {}) }}>DAILY</button>
              <button onClick={() => setFreq('weekly')} style={{ ...styles.freqBtn, ...(freq === 'weekly' ? styles.freqBtnActive : {}) }}>WEEKLY</button>
            </div>
          </div>
          {freq === 'weekly' && (
            <div style={styles.freqRow}>
              <span style={{ fontSize: 10, color: T.textMuted, letterSpacing: 1 }}>DAY:</span>
              <div style={styles.freqToggle}>
                {DAY_SHORT.map((name, idx) => (
                  <button
                    key={idx}
                    onClick={() => setEditWeeklyDay(idx)}
                    style={{ ...styles.dayBtn, ...(editWeeklyDay === idx ? styles.dayBtnActive : {}) }}
                  >{name}</button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={addCustom} style={{ ...styles.btnPrimary, flex: 1 }}>ADD</button>
            <button onClick={() => setShowAdd(false)} style={{ ...styles.btnMuted, flex: 1 }}>CANCEL</button>
          </div>
        </div>
      )}

      <div style={{ height: 16 }} />
      <button onClick={() => list.length > 0 && onSave(list)} disabled={list.length === 0} style={{
        ...styles.btnPrimary,
        opacity: list.length === 0 ? 0.3 : 1,
      }}>SAVE CHANGES</button>
    </Overlay>
  );
};

// ─── Styles ─────────────────────────────────────────────────────
const styles = {
  shell: {
    position: 'fixed',
    inset: 0,
    background: T.bg,
    overflow: 'hidden',
  },
  grain: {
    position: 'fixed',
    inset: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E")`,
    pointerEvents: 'none',
    zIndex: 1,
  },
  narrowLine: {
    position: 'fixed',
    top: 0,
    left: 24,
    width: 1,
    height: '100%',
    background: `linear-gradient(180deg, transparent 0%, ${T.gold}15 15%, ${T.gold}20 50%, ${T.gold}10 85%, transparent 100%)`,
    zIndex: 0,
  },
  container: {
    position: 'relative',
    zIndex: 2,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 480,
    margin: '0 auto',
  },

  // ── Header
  header: {
    padding: 'max(44px, env(safe-area-inset-top, 44px)) 24px 0',
    flexShrink: 0,
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  brand: { display: 'flex', alignItems: 'baseline', gap: 6 },
  title: { fontFamily: T.serif, fontSize: 26, fontWeight: 300, letterSpacing: 6, color: T.text, margin: 0 },
  titleNum: { fontFamily: T.serif, fontSize: 26, fontWeight: 600, color: T.gold, letterSpacing: 2 },
  headerActions: { display: 'flex', gap: 6 },
  headerBtn: {
    width: 34, height: 34, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface,
    color: T.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.font, transition: 'all 0.2s',
  },

  // ── Stats
  statsRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16, padding: '12px 0' },
  stat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  statValue: { fontFamily: T.serif, fontSize: 28, fontWeight: 600, color: T.text, lineHeight: 1 },
  statLabel: { fontSize: 9, fontWeight: 600, letterSpacing: 3, color: T.textMuted, fontFamily: T.font },
  statDivider: { width: 1, height: 28, background: T.border },

  // ── Progress
  progressTrack: { height: 2, background: T.goldDim, borderRadius: 1, overflow: 'hidden', position: 'relative', marginBottom: 4 },
  progressFill: { height: '100%', background: `linear-gradient(90deg, ${T.gold}80, ${T.gold})`, borderRadius: 1, transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1)' },

  // ── Week
  weekNav: { display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 },
  weekArrow: { background: 'none', border: 'none', color: T.textMuted, fontSize: 22, cursor: 'pointer', padding: '4px 6px', fontFamily: T.font, opacity: 0.6 },
  weekDays: { display: 'flex', flex: 1, justifyContent: 'center', gap: 2 },
  weekDay: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px 9px', borderRadius: 10,
    background: 'transparent', border: '1px solid transparent', cursor: 'pointer', color: T.textMuted,
    fontFamily: T.font, transition: 'all 0.25s', minWidth: 38, position: 'relative',
  },
  weekDayActive: { background: T.goldDim, border: `1px solid ${T.gold}30`, color: T.gold },
  weekDayLabel: { fontSize: 10, fontWeight: 600, letterSpacing: 1, marginBottom: 1 },
  weekDayNum: { fontSize: 15, fontWeight: 600, fontFamily: T.serif },
  todayDot: { width: 3, height: 3, borderRadius: '50%', background: T.gold, marginTop: 2 },

  // ── Complete banner
  completeBanner: { padding: '0 24px', flexShrink: 0 },
  completeInner: { textAlign: 'center', padding: '10px 0', color: T.gold, fontSize: 11, letterSpacing: 4, fontWeight: 600, fontFamily: T.font, borderBottom: `1px solid ${T.border}` },

  // ── List
  listArea: { flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '12px 20px 0' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },

  // ── Card
  card: {
    display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px', background: T.card,
    border: `1px solid ${T.border}`, borderRadius: T.radius, cursor: 'pointer', transition: 'all 0.3s ease', WebkitTapHighlightColor: 'transparent',
  },
  cardChecked: { background: T.cardDone, borderColor: T.sageBorder },
  check: {
    width: 24, height: 24, borderRadius: 7, border: `1.5px solid ${T.textMuted}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, transition: 'all 0.3s ease',
  },
  checkActive: { borderColor: T.sage, background: T.sageDim, boxShadow: `0 0 8px ${T.sage}20` },
  cardContent: { flex: 1, minWidth: 0 },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3, gap: 8 },
  cardTitle: { fontFamily: T.serif, fontSize: 17, fontWeight: 500, color: T.text, transition: 'color 0.3s' },
  cardVerse: { fontSize: 10, color: T.textMuted, whiteSpace: 'nowrap', fontFamily: T.font, letterSpacing: 0.5, flexShrink: 0 },
  cardDesc: { fontSize: 12, color: T.textSoft, margin: 0, lineHeight: 1.5, fontFamily: T.font },

  // ── Journal
  journalArea: { padding: '16px 0 8px' },
  journalBtn: {
    width: '100%', padding: '13px 20px', background: 'transparent', border: `1px dashed ${T.goldBorder}`,
    borderRadius: T.radius, color: T.textSoft, fontSize: 11, letterSpacing: 3, fontWeight: 500,
    cursor: 'pointer', fontFamily: T.font, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, position: 'relative',
  },
  journalIcon: { fontSize: 14 },
  journalDot: { width: 6, height: 6, borderRadius: '50%', background: T.gold, position: 'absolute', right: 16, top: '50%', marginTop: -3 },

  // ── Counter
  counter: { display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 2, padding: '14px 0 6px' },
  counterNum: { fontFamily: T.serif, fontSize: 32, fontWeight: 600, color: T.text },
  counterSlash: { fontFamily: T.serif, fontSize: 20, color: T.textMuted, margin: '0 2px' },
  counterTotal: { fontFamily: T.serif, fontSize: 20, color: T.textMuted },

  // ── Footer verse
  footerVerse: { textAlign: 'center', fontFamily: T.serif, fontStyle: 'italic', fontSize: 13, color: T.textMuted, lineHeight: 1.6, padding: '6px 20px 0', margin: 0 },
  footerRef: { textAlign: 'center', fontSize: 10, color: `${T.textMuted}80`, letterSpacing: 2, fontFamily: T.font, padding: '4px 0 0', margin: 0 },

  // ═══ Setup ═══
  setupScroll: { flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '0 20px' },
  setupCard: {
    display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px', background: T.card,
    border: `1px solid ${T.border}`, borderRadius: T.radius, cursor: 'pointer', transition: 'all 0.3s ease', WebkitTapHighlightColor: 'transparent',
  },
  setupCardActive: { background: T.cardDone, borderColor: T.sageBorder },
  setupCheck: {
    width: 24, height: 24, borderRadius: 7, border: `1.5px solid ${T.textMuted}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, transition: 'all 0.3s ease',
  },
  setupCheckActive: { borderColor: T.sage, background: T.sageDim },

  // ── Frequency
  freqRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px 4px 54px' },
  freqToggle: { display: 'flex', gap: 4 },
  freqBtn: {
    padding: '4px 12px', borderRadius: 6, border: `1px solid ${T.border}`, background: 'transparent',
    color: T.textMuted, fontSize: 10, letterSpacing: 2, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, transition: 'all 0.2s',
  },
  freqBtnActive: { background: T.goldDim, borderColor: `${T.gold}30`, color: T.gold },

  // ── Day picker
  dayBtn: {
    padding: '4px 7px', borderRadius: 6, border: `1px solid ${T.border}`, background: 'transparent',
    color: T.textMuted, fontSize: 9, letterSpacing: 1, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, transition: 'all 0.2s',
  },
  dayBtnActive: { background: T.goldDim, borderColor: `${T.gold}30`, color: T.gold },

  // ── Custom form
  addCustomBtn: {
    width: '100%', padding: '14px 20px', background: 'transparent', border: `1px dashed ${T.goldBorder}`,
    borderRadius: T.radius, color: T.textSoft, fontSize: 11, letterSpacing: 3, fontWeight: 500,
    cursor: 'pointer', fontFamily: T.font, transition: 'all 0.2s',
  },
  customForm: {
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 16,
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  input: {
    width: '100%', padding: '10px 14px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
    color: T.text, fontFamily: T.font, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  },
  startBtn: {
    display: 'block', width: '100%', padding: 16, background: T.sageDim, border: `1px solid ${T.sageBorder}`,
    borderRadius: 12, color: T.sage, fontSize: 12, letterSpacing: 3, fontWeight: 600, fontFamily: T.font, cursor: 'pointer',
  },

  // ═══ Modal / Overlay ═══
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(5, 4, 3, 0.88)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20, backdropFilter: 'blur(6px)',
  },
  modal: {
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16,
    width: '100%', maxWidth: 420, maxHeight: '82vh', overflowY: 'auto', padding: '20px 24px 24px',
  },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  modalTitle: { fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.text, margin: 0, letterSpacing: 1 },
  modalSub: { fontSize: 12, color: T.textMuted, margin: '4px 0 0', fontFamily: T.font },
  modalClose: { background: 'none', border: 'none', color: T.textMuted, fontSize: 18, cursor: 'pointer', padding: 4, marginTop: -2, fontFamily: T.font },

  // ── Textarea
  textarea: {
    width: '100%', height: 200, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10,
    color: T.text, fontFamily: T.serif, fontSize: 15, padding: 16, lineHeight: 1.7, resize: 'none', outline: 'none', marginBottom: 14, boxSizing: 'border-box',
  },

  // ── Buttons
  btnPrimary: {
    display: 'block', width: '100%', padding: 14, background: T.sageDim, border: `1px solid ${T.sageBorder}`,
    borderRadius: 10, color: T.sage, fontSize: 11, letterSpacing: 3, fontWeight: 600, cursor: 'pointer', fontFamily: T.font,
  },
  btnWarn: {
    display: 'block', width: '100%', padding: 14, background: T.warnDim, border: '1px solid rgba(184, 137, 90, 0.2)',
    borderRadius: 10, color: T.warn, fontSize: 11, letterSpacing: 3, fontWeight: 600, cursor: 'pointer', fontFamily: T.font,
  },
  btnDanger: {
    display: 'block', width: '100%', padding: 14, background: T.dangerDim, border: '1px solid rgba(138, 74, 74, 0.15)',
    borderRadius: 10, color: T.danger, fontSize: 11, letterSpacing: 3, fontWeight: 600, cursor: 'pointer', fontFamily: T.font,
  },
  btnMuted: {
    display: 'block', width: '100%', padding: 14, background: 'transparent', border: `1px solid ${T.border}`,
    borderRadius: 10, color: T.textMuted, fontSize: 11, letterSpacing: 3, fontWeight: 600, cursor: 'pointer', fontFamily: T.font,
  },

  // ── History
  historyList: { maxHeight: '55vh', overflowY: 'auto' },
  historyRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' },
  historyLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  historyDay: { fontFamily: T.serif, fontSize: 16, fontWeight: 500, color: T.text },
  historyDate: { fontSize: 11, color: T.textMuted, fontFamily: T.font },
  historyRight: { display: 'flex', alignItems: 'center', fontFamily: T.serif, fontSize: 14 },

  // ── Info / About
  infoContent: { lineHeight: 1.6 },
  infoVerse: { fontFamily: T.serif, fontStyle: 'italic', fontSize: 15, color: T.textSoft, margin: '0 0 6px', lineHeight: 1.7, borderLeft: `2px solid ${T.gold}30`, paddingLeft: 16 },
  infoRef: { fontSize: 11, color: T.textMuted, letterSpacing: 1, margin: '0 0 24px', fontFamily: T.font },
  infoHeading: { fontFamily: T.serif, fontSize: 16, fontWeight: 500, color: T.gold, margin: '0 0 10px', letterSpacing: 1 },
  infoText: { fontSize: 13, color: T.textSoft, margin: '0 0 20px', fontFamily: T.font, lineHeight: 1.6 },
  infoItem: { display: 'flex', flexDirection: 'column', padding: '8px 0', borderBottom: `1px solid ${T.border}`, marginBottom: 4 },
  infoItemTitle: { fontFamily: T.serif, fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 2 },
  infoItemDesc: { fontSize: 12, color: T.textMuted, fontFamily: T.font, lineHeight: 1.5 },
};

export default App;
